import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Button, Modal, Tooltip } from 'antd';
import { LuSpline } from 'react-icons/lu';
import WidgetContent from './WidgetContent';
import {
	calculateSagittaWithTolerance,
	convertValue,
	validateInputs,
} from './calculations';
import { fieldNumericError, parseLenientNumber } from './inputParsing';
import type { ArcInputs, SagittaResult, UnitSystem } from './types';

function formatConverted(n: number): string {
	return String(Number(n.toPrecision(12)));
}

function defaultStr(n: number | undefined): string {
	if (n == null || !Number.isFinite(n)) return '';
	return String(n);
}

export interface ArcSagittaWidgetProps {
	onCalculate?: (result: SagittaResult) => void;
	defaultValues?: Partial<ArcInputs>;
	buttonText?: string;
	buttonProps?: React.ComponentProps<typeof Button>;
	/** Shown on hover over the button; use with an empty buttonText for an icon-only button. */
	tooltip?: React.ReactNode;
}

export function ArcSagittaWidget({
	onCalculate,
	defaultValues,
	buttonText = 'Arc Sagitta Calculator',
	buttonProps,
	tooltip,
}: ArcSagittaWidgetProps) {
	const appliedDefaults = useRef(false);

	const [open, setOpen] = useState(false);
	const [unit, setUnit] = useState<UnitSystem>(
		defaultValues?.unit ?? 'inches'
	);
	const [inputMode, setInputMode] = useState<'radius' | 'diameter'>(
		defaultValues?.inputMode ?? 'radius'
	);
	const [chordStr, setChordStr] = useState(() =>
		defaultStr(defaultValues?.chordLength ?? undefined)
	);
	const [radiusOrDiameterStr, setRadiusOrDiameterStr] = useState(() =>
		defaultStr(defaultValues?.radiusOrDiameter ?? undefined)
	);
	const [tolPlusStr, setTolPlusStr] = useState(() =>
		defaultStr(defaultValues?.tolPlus ?? undefined)
	);
	const [tolMinusStr, setTolMinusStr] = useState(() =>
		defaultStr(defaultValues?.tolMinus ?? undefined)
	);

	const [chordFocused, setChordFocused] = useState(false);
	const [radiusDiaFocused, setRadiusDiaFocused] = useState(false);

	useEffect(() => {
		if (appliedDefaults.current || !defaultValues) return;
		appliedDefaults.current = true;
		if (defaultValues.unit != null) setUnit(defaultValues.unit);
		if (defaultValues.inputMode != null) setInputMode(defaultValues.inputMode);
		if (defaultValues.chordLength != null)
			setChordStr(String(defaultValues.chordLength));
		if (defaultValues.radiusOrDiameter != null)
			setRadiusOrDiameterStr(String(defaultValues.radiusOrDiameter));
		if (defaultValues.tolPlus != null)
			setTolPlusStr(String(defaultValues.tolPlus));
		if (defaultValues.tolMinus != null)
			setTolMinusStr(String(defaultValues.tolMinus));
	}, [defaultValues]);

	const handleReset = useCallback(() => {
		setChordStr('');
		setRadiusOrDiameterStr('');
		setTolPlusStr('');
		setTolMinusStr('');
	}, []);

	const handleUnitChange = useCallback(
		(newUnit: UnitSystem) => {
			if (newUnit === unit) return;
			const conv = (s: string) => {
				const n = parseLenientNumber(s);
				if (n === null) return s;
				return formatConverted(convertValue(n, unit, newUnit));
			};
			setChordStr((prev) => conv(prev));
			setRadiusOrDiameterStr((prev) => conv(prev));
			setTolPlusStr((prev) => conv(prev));
			setTolMinusStr((prev) => conv(prev));
			setUnit(newUnit);
		},
		[unit]
	);

	const derived = useMemo(() => {
		const placeholder = 'Enter chord length and radius to calculate.';

		const chordError = fieldNumericError(chordStr, 'positive');
		const radiusError = fieldNumericError(radiusOrDiameterStr, 'positive');
		const tolPlusError = fieldNumericError(tolPlusStr, 'nonnegative');
		const tolMinusError = fieldNumericError(tolMinusStr, 'nonnegative');

		const cRaw = parseLenientNumber(chordStr);
		const rRaw = parseLenientNumber(radiusOrDiameterStr);
		const Rnum =
			rRaw != null && rRaw > 0
				? inputMode === 'diameter'
					? rRaw / 2
					: rRaw
				: null;

		const displayChord = chordFocused
			? null
			: cRaw != null && cRaw > 0
				? cRaw
				: null;
		const displayRadius = radiusDiaFocused ? null : Rnum;

		const baseReturn = {
			result: null as SagittaResult | null,
			chordError,
			radiusError,
			tolPlusError,
			tolMinusError,
			geometryError: null as string | null,
			toleranceError: null as string | null,
			displayRadius,
			displayChord,
			radiusOrDiameterField: rRaw != null && rRaw > 0 ? rRaw : null,
			placeholder,
		};

		// While typing, surface per-field errors but skip cross-field geometry
		// checks and result computation to avoid mid-keystroke flicker.
		if (chordFocused || radiusDiaFocused) return baseReturn;

		// Any per-field problem blocks computation.
		if (chordError || radiusError || tolPlusError || tolMinusError) {
			return baseReturn;
		}

		if (cRaw == null || rRaw == null || Rnum == null) return baseReturn;

		const tp = parseLenientNumber(tolPlusStr);
		const tm = parseLenientNumber(tolMinusStr);
		const skipTol = tolPlusStr.trim() === '' && tolMinusStr.trim() !== '';
		let tolPlus: number | undefined;
		let tolMinus: number | undefined;
		if (skipTol || tolPlusStr.trim() === '') {
			tolPlus = undefined;
			tolMinus = undefined;
		} else {
			tolPlus = tp ?? undefined;
			tolMinus = tolMinusStr.trim() === '' ? undefined : (tm ?? 0);
		}

		const geoErrs = validateInputs(Rnum, cRaw, tolPlus, tolMinus);
		if (geoErrs.length) {
			const tolErr = geoErrs.find((e) => e.includes('Minus tolerance'));
			const main = geoErrs.find((e) => !e.includes('Minus tolerance'));
			return {
				...baseReturn,
				geometryError: main ?? null,
				toleranceError: tolErr ?? null,
			};
		}

		return {
			...baseReturn,
			result: calculateSagittaWithTolerance(Rnum, cRaw, tolPlus, tolMinus),
		};
	}, [
		chordFocused,
		radiusDiaFocused,
		chordStr,
		radiusOrDiameterStr,
		tolPlusStr,
		tolMinusStr,
		inputMode,
	]);

	const {
		result,
		chordError,
		radiusError,
		tolPlusError,
		tolMinusError,
		geometryError,
		toleranceError,
		displayRadius,
		displayChord,
		radiusOrDiameterField,
		placeholder,
	} = derived;

	const lastEmitted = useRef<string>('');
	useEffect(() => {
		if (!result || Number.isNaN(result.nominal)) return;
		const key = JSON.stringify({
			n: result.nominal,
			u: result.upperLimit,
			l: result.lowerLimit,
		});
		if (key === lastEmitted.current) return;
		lastEmitted.current = key;
		onCalculate?.(result);
	}, [result, onCalculate]);

	const button = (
		<Button
			type="default"
			icon={<LuSpline />}
			onClick={() => setOpen(true)}
			{...buttonProps}
		>
			{buttonText || null}
		</Button>
	);

	return (
		<>
			{tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button}
			<Modal
				title="Arc Sagitta Calculator"
				open={open}
				onCancel={() => setOpen(false)}
				footer={null}
				width="min(90vw, 900px)"
				destroyOnClose={false}
				maskClosable
			>
				<WidgetContent
					unit={unit}
					onUnitChange={handleUnitChange}
					inputMode={inputMode}
					onInputModeChange={setInputMode}
					chordStr={chordStr}
					onChordStrChange={setChordStr}
					onChordFocus={() => setChordFocused(true)}
					onChordBlur={() => setChordFocused(false)}
					radiusOrDiameterStr={radiusOrDiameterStr}
					onRadiusOrDiameterStrChange={setRadiusOrDiameterStr}
					onRadiusDiaFocus={() => setRadiusDiaFocused(true)}
					onRadiusDiaBlur={() => setRadiusDiaFocused(false)}
					tolPlusStr={tolPlusStr}
					onTolPlusStrChange={setTolPlusStr}
					tolMinusStr={tolMinusStr}
					onTolMinusStrChange={setTolMinusStr}
					chordError={chordError}
					radiusError={radiusError}
					tolPlusError={tolPlusError}
					tolMinusError={tolMinusError}
					geometryError={geometryError}
					toleranceError={toleranceError}
					result={result}
					placeholder={placeholder}
					displayRadius={displayRadius}
					displayChord={displayChord}
					radiusOrDiameterField={radiusOrDiameterField}
					onReset={handleReset}
				/>
			</Modal>
		</>
	);
}

export default ArcSagittaWidget;
